import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { KeyRound, Loader2, ArrowLeft, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const { user, changePassword, logout, forcePasswordChange } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (next.length < 10) {
      setError("Nowe hasło musi mieć minimum 10 znaków");
      return;
    }
    if (next !== confirm) {
      setError("Hasła nie pasują");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Hasło zmienione. Zaloguj się ponownie.");
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.detail || "Błąd zmiany hasła");
    } finally {
      setLoading(false);
    }
  }

  async function handleBack() {
    if (forcePasswordChange) {
      // Forced flow → safest action is logout, can't continue without changing.
      try { await logout(); } catch { /* ignore */ }
      navigate("/login", { replace: true });
    } else {
      navigate(-1);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tt-bg)] p-6">
      <Card className="tt-card w-full max-w-md" data-testid="change-password-page">
        <CardHeader>
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-sm text-[var(--tt-muted)] hover:text-[var(--tt-text)] mb-2 self-start tt-focusable"
            data-testid="change-password-back-button"
          >
            {forcePasswordChange ? (
              <><LogOut className="h-3.5 w-3.5" /> Wyloguj</>
            ) : (
              <><ArrowLeft className="h-3.5 w-3.5" /> Wróć</>
            )}
          </button>
          <CardTitle className="tt-display">Zmiana hasła</CardTitle>
          <CardDescription>
            {forcePasswordChange
              ? `${user?.email}: wymagana zmiana hasła przy pierwszym logowaniu.`
              : `${user?.email}: zmień obecne hasło na nowe.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Obecne hasło</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Nowe hasło (min 10 znaków)</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Potwierdź nowe hasło</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input" />
            </div>
            {error && (<Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>)}
            <Button type="submit" className="w-full" disabled={loading} data-testid="change-password-submit-button">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Zmień hasło
            </Button>
            {!forcePasswordChange && (
              <Button type="button" variant="ghost" className="w-full" onClick={() => navigate(-1)} data-testid="change-password-cancel-button">
                Anuluj
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
